import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { parseISO, format } from "date-fns";
import {
  fetchScheduleItemsWithChangeRequests,
  fetchPartners,
  fetchUsers,
} from "api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "components/common";
import { ActionsMenu }        from "./ActionsMenu";
import { AddNoteModal }       from "./AddNoteModal";
import { AssignCourierModal } from "./AssignCourierModal";
import { ChangeStatusModal }  from "./ChangeStatusModal";
import { DeleteModal }        from "./DeleteModal";
import { ChangeRequestRow }   from "./ChangeRequestRow";
import type { ScheduleItem }  from "types";

type CRStatusFilter = "requested" | "approved" | "denied" | "all";

const STATUS_TABS: { value: CRStatusFilter; label: string }[] = [
  { value: "requested", label: "Open" },
  { value: "all",       label: "All" },
  { value: "approved",  label: "Approved" },
  { value: "denied",    label: "Denied" },
];

const COURIER_COLORS = [
  "#6366f1", "#0891b2", "#059669", "#d97706",
  "#dc2626", "#7c3aed", "#2563eb", "#c026d3",
];

export default function ChangeRequestsPage() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<CRStatusFilter>("requested");
  const [activeMenu,   setActiveMenu]   = useState<number | null>(null);
  const [noteTarget,     setNoteTarget]     = useState<ScheduleItem | null>(null);
  const [courierTarget,  setCourierTarget]  = useState<ScheduleItem | null>(null);
  const [statusTarget,   setStatusTarget]   = useState<ScheduleItem | null>(null);
  const [deleteTarget,   setDeleteTarget]   = useState<ScheduleItem | null>(null);

  const queryParam = statusFilter === "all" ? undefined : statusFilter;

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["scheduleItems", "changeRequests", statusFilter],
    queryFn:  () => fetchScheduleItemsWithChangeRequests(queryParam),
  });

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ["users"],
    queryFn:  () => fetchUsers(),
  });

  const partnerNames = React.useMemo(() => {
    const map: Record<number, string> = {};
    partners.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [partners]);

  const partnerTypes = React.useMemo(() => {
    const map: Record<number, string> = {};
    partners.forEach((p) => { map[p.id] = p.type; });
    return map;
  }, [partners]);

  const courierInfo = React.useMemo(() => {
    const nameMap:  Record<number, string> = {};
    const colorMap: Record<number, string> = {};
    const couriers = allUsers.filter((u) => u.role === "bridge13_courier" && u.active);
    couriers.forEach((u, i) => {
      nameMap[u.id]  = `${u.name_1}${u.name_2 ? ` ${u.name_2}` : ""}`;
      colorMap[u.id] = COURIER_COLORS[i % COURIER_COLORS.length];
    });
    return { names: nameMap, colors: colorMap };
  }, [allUsers]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["scheduleItems"] });
  };

  // Sort by scheduled_at ascending (soonest first — most actionable)
  const sorted = [...items].sort(
    (a, b) => parseISO(a.scheduled_at).getTime() - parseISO(b.scheduled_at).getTime(),
  );

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-3xl">
      <PageHeader
        title="Change requests"
        subtitle="Schedule items with partner-submitted change requests."
      />

      {/* ── Status filter tabs ─────────────────────────────────────────────── */}
      <div className="flex gap-1 mb-6 p-1 bg-gray-100 rounded-lg w-fit">
        {STATUS_TABS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setStatusFilter(value)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              statusFilter === value
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── List ──────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner className="mt-16" />
      ) : sorted.length === 0 ? (
        <EmptyState message={statusFilter === "requested" ? "No open change requests." : "No change requests found."} />
      ) : (
        <div className="space-y-2">
          {sorted.map((item) => {
            const partnerName = partnerNames[item.partner_entity_id] ?? `Partner #${item.partner_entity_id}`;
            const partnerType = partnerTypes[item.partner_entity_id];
            const isPickup    = partnerType !== "distributor";
            const actionLabel = isPickup ? "Pickup" : "Drop off";
            const actionVerb  = isPickup ? "from" : "at";

            // Show all change requests (page-level filter already scoped items)
            const changeRequests = item.change_requests ?? [];

            return (
              <div
                key={item.id}
                className="card flex"
                style={
                  item.contact_user_id && courierInfo.colors[item.contact_user_id]
                    ? { borderLeft: `4px solid ${courierInfo.colors[item.contact_user_id]}` }
                    : { borderLeft: "4px solid transparent" }
                }
              >
                <div className="flex-1">
                  {/* ── Item header row ─────────────────────────────────── */}
                  <div
                    onClick={() => navigate(`/schedule-items/${item.id}`)}
                    className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    {/* Courier avatar */}
                    {item.contact_user_id && (
                      <div className="shrink-0">
                        {item.contact_user?.profile_photo_url ? (
                          <img
                            src={item.contact_user.profile_photo_url}
                            className="w-8 h-8 rounded-full object-cover"
                            alt=""
                          />
                        ) : (
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                            style={{ backgroundColor: courierInfo.colors[item.contact_user_id] ?? "#9ca3af" }}
                          >
                            {(courierInfo.names[item.contact_user_id] ?? "?").charAt(0)}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-xl font-medium ${
                          isPickup ? "bg-green-100 text-green-700" : "bg-indigo-100 text-indigo-700"
                        }`}>
                          {actionLabel}
                        </span>
                        {actionVerb} {partnerName}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {format(parseISO(item.scheduled_at), "EEE MMM d, yyyy")}
                        {" · "}
                        {parseISO(item.scheduled_at).toLocaleTimeString([], {
                          hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
                        })}
                        {item.contact_user_id && courierInfo.names[item.contact_user_id] && (
                          <>
                            {" · "}
                            <span
                              style={{ color: courierInfo.colors[item.contact_user_id] }}
                              className="font-medium"
                            >
                              {courierInfo.names[item.contact_user_id]}
                            </span>
                          </>
                        )}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <StatusBadge status={item.status} />
                      <ActionsMenu
                        isOpen={activeMenu === item.id}
                        onToggle={(e) => {
                          e.stopPropagation();
                          setActiveMenu(activeMenu === item.id ? null : item.id);
                        }}
                        onClose={() => setActiveMenu(null)}
                        onAddNote={() => { setActiveMenu(null); setNoteTarget(item); }}
                        onAssignCourier={() => { setActiveMenu(null); setCourierTarget(item); }}
                        onChangeStatus={() => { setActiveMenu(null); setStatusTarget(item); }}
                        onDelete={() => { setActiveMenu(null); setDeleteTarget(item); }}
                      />
                    </div>
                  </div>

                  {/* ── Change request rows ──────────────────────────────── */}
                  {changeRequests.length > 0 && (
                    <div className="border-t border-amber-100 px-4 py-2.5 space-y-2 bg-amber-50/40">
                      {changeRequests.map((cr) => (
                        <ChangeRequestRow
                          key={cr.id}
                          request={cr}
                          scheduleItemId={item.id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {noteTarget && (
        <AddNoteModal
          item={noteTarget}
          partnerName={partnerNames[noteTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setNoteTarget(null)}
          onSuccess={() => { invalidate(); setNoteTarget(null); }}
        />
      )}
      {courierTarget && (
        <AssignCourierModal
          item={courierTarget}
          partnerName={partnerNames[courierTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setCourierTarget(null)}
          onSuccess={() => { invalidate(); setCourierTarget(null); }}
        />
      )}
      {statusTarget && (
        <ChangeStatusModal
          item={statusTarget}
          partnerName={partnerNames[statusTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setStatusTarget(null)}
          onSuccess={() => { invalidate(); setStatusTarget(null); }}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          item={deleteTarget}
          partnerName={partnerNames[deleteTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => { invalidate(); setDeleteTarget(null); }}
        />
      )}
    </div>
  );
}
